export const APP_LOCALE_COOKIE = "cv_app_locale";

export const appLocales = ["en", "fr"] as const;

export type AppLocale = (typeof appLocales)[number];

export function isAppLocale(value: string | undefined): value is AppLocale {
  return appLocales.some((locale) => locale === value);
}

/**
 * Picks the best-matching supported locale from a browser's Accept-Language
 * header (e.g. "fr-CA,fr;q=0.9,en-US;q=0.8,en;q=0.7"), falling back to "en"
 * when the header is absent or none of its entries match a supported locale.
 */
export function resolvePreferredAppLocale(acceptLanguageHeader: string | null): AppLocale {
  if (!acceptLanguageHeader) {
    return "en";
  }

  const ranked = acceptLanguageHeader
    .split(",")
    .map((entry) => {
      const [rawTag, rawQuality] = entry.trim().split(";q=");
      const quality = rawQuality ? Number.parseFloat(rawQuality) : 1;
      const primarySubtag = rawTag?.trim().split("-")[0]?.toLowerCase();

      return { primarySubtag, quality: Number.isFinite(quality) ? quality : 0 };
    })
    .sort((a, b) => b.quality - a.quality);

  const match = ranked.find((entry) => isAppLocale(entry.primarySubtag));

  return match && isAppLocale(match.primarySubtag) ? match.primarySubtag : "en";
}

/**
 * Writes the app-wide locale cookie from outside AppLocaleProvider — e.g. a marketing
 * page CTA that navigates into the tenant app should carry its locale forward, since the
 * two surfaces don't share a locale mechanism otherwise.
 */
export function persistAppLocaleCookie(locale: AppLocale): void {
  document.documentElement.lang = locale;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${APP_LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}
